import { useEffect } from 'react';

import { DispatchPagination, PaginationState, paginationToApi } from '@/hooks/use-pagination';
import api from '@/services/api/api';
import PaginationData from '@/types/Api/PaginationData';
import { ResponsePaginationData } from '@/types/Api/ResponsePaginationData';
import { keepPreviousData, QueryKey, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { useAbility } from './ability';

export type PaginatedQueryParams = Partial<PaginationState> & { dispatchPagination: DispatchPagination };

type Props<T> = {
  permissions?: string[] | string;
  params?: { [key: string]: any; params?: { [key: string]: any } } & PaginatedQueryParams;
  url: string;
  onSuccess?: (response: any) => void;
  needAllPermissions?: boolean;
} & UseQueryOptions<PaginationData<T>, Error, PaginationData<T>, QueryKey>;

export const usePaginatedQuery = <T>({ needAllPermissions, ...props }: Props<T>) => {
  const ability = useAbility();

  const { permissions, onSuccess } = props;

  const getHasPermission = () => {
    if (Array.isArray(permissions)) {
      if (needAllPermissions && !permissions.every(permission => ability.can(permission, ''))) return false;
      if (!needAllPermissions && !permissions.some(permission => ability.can(permission, ''))) return false;
    } else if (permissions && !ability.can(permissions, '')) return false;
    return true;
  };

  const query = useQuery<PaginationData<T>>({
    ...props,
    placeholderData: keepPreviousData,
    queryKey: [
      ...props.queryKey,
      `page=${props.params?.current}&pageSize=${props.params?.pageSize}&search=${props.params?.search}`,
      props.params?.sort,
      props.params?.filter,
    ],
    enabled: props.enabled && !!getHasPermission(),
    queryFn: async ({ signal }) => {
      const response = await api.get<ResponsePaginationData<T> | PaginationData<T>>(props.url, {
        params: {
          ...paginationToApi(props.params ?? {}),
          ...(props.params || { params: {} })?.params,
          ...(props.params?.sort ? props.params.sort : {}),
        },
        signal,
      });
      if (response.status !== 200) throw new Error('Error');

      let data: PaginationData<T>;
      if ('message' in response.data) data = response.data.data as PaginationData<T>;
      else data = response.data;

      if (!data) return { data: [], total: 0, last_page: 1, current_page: 1 };

      const { total, last_page } = data?.meta ?? data;

      props.params?.dispatchPagination({
        type: 'SET_TOTAL',
        payload: total ?? 0,
      });

      props.params?.dispatchPagination({
        type: 'SET_LAST_PAGE',
        payload: last_page ?? 0,
      });

      if (onSuccess) onSuccess(data);

      return data;
    },
  });

  useEffect(() => {
    if (query.data) {
      const { total, last_page } = query.data?.meta ?? query.data;
      props.params?.dispatchPagination({
        type: 'SET_TOTAL',
        payload: total ?? 0,
      });

      props.params?.dispatchPagination({
        type: 'SET_LAST_PAGE',
        payload: last_page ?? 0,
      });
    }
  }, [query.data]);

  return query;
};
